import HttpClient from './utils/HttpClient';
import APIError from '../errors/APIError';

import ListOrdersServiceResponse from '../interfaces/IListOrders';
import { IOrder } from '../interfaces/IOrder';
import { IDeliveryAddress } from '../interfaces/IDeliveryAddress';
import { IPaymentMethod } from '../interfaces/IPaymentMethod';
import { IOrderItem } from '../interfaces/IOrderItem';

type IComplement = {
  id: number;
  quantity: number;
};

type IChildOrderItem = {
  productId: number;
  quantity: number;
  observation?: string;
};

type IItem = {
  productId: number;
  sizeId?: number;
  gridId?: number;
  observation?: string;
  quantity: number;
  complements: IComplement[];
  childOrderItems: IChildOrderItem[];
};

type CreateOrderServiceRequest = {
  orderType: 'DELIVERY' | 'INDOOR' | 'TAKEOUT';
  companyId: number;
  selectedCompanyId: number;
  companyPaymentMethodId: number;
  deliveryAddressId?: number;
  customerId?: number;
  cashierId: number;
  extraDiscount: number;
  discountCouponId?: number;
  changeMoney?: number;
  observation?: string;
  scheduledAt?: string;
  deliveryFeeId?: number;
  items: IItem[];
};

type EditOrderServiceRequest = Omit<
  CreateOrderServiceRequest,
  'selectedCompanyId'
> & {
  id: number;
};

export type IOrderToEdit = {
  orderType: 'DELIVERY' | 'INDOOR' | 'TAKEOUT';
  changeMoney: string;
  companyPaymentMethod: {
    id: number;
    paymentMethod: {
      description: string;
    };
  };
  note: string;
  items: any[]; // ??
  customer: {
    id: number;
    tradingName: string;
  }; // * pedido sem cliente
  deliveryAddressId: number | null;
  address:
    | {}
    | {
        cep: string;
        bairro: string;
        cidade: string;
        estado: string;
        numero: string;
        logradouro: string;
        complemento: string;
      };
  deliveryFee: string;
  discountCouponId: number | null;
  othersDiscountsValue: string | null;
  scheduledDeliveryAt: string | null;
  createdAt: string;
  totalDiscount: string;
};

interface IOrderItemForQuantityEdit {
  orderId?: number;
  cashierId: number;
  companyId: number;
  orderItems: {
    id: number;
    quantity: number;
  }[];
}

class OrdersService {
  private httpClient = HttpClient;

  public async listOrders(
    companyId: number,
    cashierId: number,
    page: number,
    limit: number,
    status: string,
    customerName?: string,
  ): Promise<ListOrdersServiceResponse> {
    try {
      const { data } = await this.httpClient.get(
        `/companies/${companyId}/cashiers/${cashierId}/orders?page=${page}&limit=${limit}&status=${status}&customerName=${
          customerName || ''
        }`,
      );

      data.orders.sort((a: any, b: any) => b.id - a.id);

      return data;
    } catch (error) {
      const parsedError = error as any;

      let errorMessage = parsedError?.response?.data?.message;

      if (!errorMessage) {
        errorMessage = 'Erro ao tentar listar os pedidos';
      }

      throw new APIError(errorMessage);
    }
  }

  public async showOrder(
    companyId: number,
    cashierId: number,
    orderId: number,
  ): Promise<
    Partial<IOrder> & {
      deliveryAddress?: Partial<IDeliveryAddress>;
      orderPayment: Partial<IPaymentMethod>;
      items: Partial<IOrderItem>[];
    }
  > {
    try {
      const response = await this.httpClient.get(
        `/companies/${companyId}/cashiers/${cashierId}/orders/${orderId}`,
      );

      return response.data;
    } catch (error) {
      const parsedError = error as any;

      let errorMessage = parsedError?.response?.data?.message;

      if (!errorMessage) {
        errorMessage = 'Erro ao tentar obter os detalhes do pedido';
      }

      throw new APIError(errorMessage);
    }
  }

  public async showOrderToEdit(
    companyId: number,
    cashierId: number,
    orderId: number,
  ): Promise<IOrderToEdit> {
    try {
      const response = await this.httpClient.get(
        `/companies/${companyId}/cashiers/${cashierId}/orders/edit/${orderId}`,
      );

      return response.data;
    } catch (error) {
      const parsedError = error as any;

      let errorMessage = parsedError?.response?.data?.message;

      if (!errorMessage) {
        errorMessage = 'Erro ao tentar obter os detalhes do pedido';
      }

      throw new APIError(errorMessage);
    }
  }

  public async editOrderStatus(
    companyId: number,
    cashierId: number,
    orderId: number,
    status: number,
  ): Promise<
    Partial<IOrder> & {
      deliveryAddress?: Partial<IDeliveryAddress>;
      paymentMethod: Partial<IPaymentMethod>;
      items: Partial<IOrderItem>[];
    }
  > {
    try {
      const response = await this.httpClient.patch(
        `/companies/${companyId}/cashiers/${cashierId}/orders/${orderId}`,
        { status },
      );

      return response.data;
    } catch (error) {
      const parsedError = error as any;

      let errorMessage = parsedError?.response?.data?.message;

      if (!errorMessage) {
        errorMessage = 'Erro ao tentar editar o estágio do pedido';
      }

      throw new APIError(errorMessage);
    }
  }

  public async createPagBankPix(
    companyId: number,
    cashierId: number,
    orderId: number,
  ): Promise<{
    orderId: number;
    status: string;
    qrCode?: string;
    copyPaste?: string;
    expirationDate?: string;
  }> {
    try {
      const response = await this.httpClient.post(
        `/companies/${companyId}/cashiers/${cashierId}/orders/${orderId}/pagbank/pix`,
        {},
      );

      return response.data;
    } catch (error) {
      const parsedError = error as any;
      throw new APIError(
        parsedError?.response?.data?.message ||
          'Erro ao gerar cobrança PIX PagBank',
      );
    }
  }

  public async createOrder(payload: CreateOrderServiceRequest): Promise<any> {
    const {
      cashierId,
      companyId,
      companyPaymentMethodId,
      extraDiscount,
      selectedCompanyId,
      items,
      orderType,
      changeMoney,
      customerId,
      deliveryAddressId,
      deliveryFeeId,
      discountCouponId,
      observation,
      scheduledAt,
    } = payload;

    try {
      const response = await this.httpClient.post(
        `/companies/${companyId}/cashiers/${cashierId}/orders`,
        {
          companyPaymentMethodId,
          extraDiscount,
          selectedCompanyId,
          items,
          orderType,
          changeMoney,
          customerId,
          deliveryAddressId,
          deliveryFeeId,
          discountCouponId,
          observation,
          scheduledAt,
        },
      );

      return response.data;
    } catch (error) {
      const parsedError = error as any;

      let errorMessage = parsedError?.response?.data?.message;

      if (!errorMessage) {
        errorMessage = 'Erro ao tentar criar o pedido';
      }

      throw new APIError(errorMessage);
    }
  }

  public async editOrder(payload: EditOrderServiceRequest): Promise<any> {
    const {
      id,
      cashierId,
      companyId,
      companyPaymentMethodId,
      extraDiscount,
      items,
      orderType,
      changeMoney,
      customerId,
      deliveryAddressId,
      deliveryFeeId,
      discountCouponId,
      observation,
      scheduledAt,
    } = payload;

    try {
      const response = await this.httpClient.put(
        `/companies/${companyId}/cashiers/${cashierId}/orders/${id}`,
        {
          companyPaymentMethodId,
          extraDiscount,
          items,
          orderType,
          changeMoney,
          customerId,
          deliveryAddressId,
          deliveryFeeId,
          discountCouponId,
          observation,
          scheduledAt,
        },
      );

      return response.data;
    } catch (error) {
      const parsedError = error as any;

      let errorMessage = parsedError?.response?.data?.message;

      if (!errorMessage) {
        errorMessage = 'Erro ao tentar criar o pedido';
      }

      throw new APIError(errorMessage);
    }
  }

  public async editOrderItems(
    payload: IOrderItemForQuantityEdit,
  ): Promise<any> {
    const { orderId, cashierId, companyId, orderItems } = payload;

    try {
      const response = await this.httpClient.put(
        `/companies/${companyId}/cashiers/${cashierId}/orders/${orderId}/items`,
        {
          orderItems,
        },
      );

      return response.data;
    } catch (error) {
      const parsedError = error as any;

      let errorMessage = parsedError?.response?.data?.message;

      if (!errorMessage) {
        errorMessage = 'Erro ao tentar atualizar os itens do pedido';
      }

      throw new APIError(errorMessage);
    }
  }

  public async showPdvOrder(
    companyId: number,
    orderId: number,
  ): Promise<{
    displayId: number;
    customer: {
      name: string;
      phone: string;
    };
    paymentMethods: Array<{ description: string }>;
    orderItems: Array<{
      id: string;
      productName: string;
      quantity: number;
      totalPrice: number;
    }>;
    value: number;
    changeFor: number;
    observation: string;
  }> {
    try {
      const response = await this.httpClient.get(
        `/pdv/companies/${companyId}/orders/${orderId}`,
      );

      return response.data;
    } catch (error) {
      const parsedError = error as any;

      let errorMessage = parsedError?.response?.data?.message;

      if (!errorMessage) {
        errorMessage = 'Erro ao tentar obter os detalhes do pedido PDV';
      }

      throw new APIError(errorMessage);
    }
  }
}

export default new OrdersService();
